import {
  manuallyRefundOrder,
  getOrderHistory,
  markOrderManualRefundPending,
  partiallyRefundOrder,
  refundOrder,
  refreshRefundStatus,
} from '../../../api/orderHistoryApi'
import LinkButton from '../../../components/link-button/link-button'
import config from '../../../config'
import { getDateTime } from '../../../helpers/utils'
import AuthPermission from '../../../high-order-components/auth-permission'
import translate from '../../../i18n'
import { PermissionsEnum } from '../../../share-types'
import { formatOrderHistoryStatus } from '../order-history-status'
import { OrderHistory, OrderHistoryRefundRequest } from '../share-type'
import OrderRefundModal from './order-refund-modal'
import { Alert, Button, Card, Col, Descriptions, Modal, Row, Space, notification } from 'antd'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

const manualRefundMinimumOrderAgeInDays = 365
const manualRefundMinimumOrderAgeInMilliseconds =
  manualRefundMinimumOrderAgeInDays * 24 * 60 * 60 * 1000
const { guavapassUrl } = config

type RefundMode = 'partial' | 'manual'

const OrderHistoryDetails = () => {
  const { id } = useParams()
  const [order, setOrder] = useState<OrderHistory>()
  const [refundMode, setRefundMode] = useState<RefundMode>()

  const loadOrder = () => {
    if (id) {
      getOrderHistory(Number(id)).then(setOrder)
    }
  }

  useEffect(loadOrder, [id])

  const execute = (action: () => Promise<unknown>) =>
    action().then(() => {
      notification.success({ title: translate('operationSuccess') })
      loadOrder()
    })

  if (!order) {
    return null
  }

  const planPurchase = order.orderType === 'PLAN_PURCHASE'
  const refundable = planPurchase && order.status === 'PAID'
  const manualPending = planPurchase && order.status === 'MANUAL_PARTIAL_REFUND_PENDING'
  const userCredits = order.userCredits ?? 0
  const purchasedCredits = order.purchasedCredits ?? 0
  const refundableCredits = order.refundableCredits ?? Math.min(userCredits, purchasedCredits)
  const maximumRefundAmountInCents = order.maximumRefundAmountInCents ?? order.totalFee ?? 0
  const refundDetailsAvailable = order.userCredits != null && order.purchasedCredits != null
  const fullRefundAvailable =
    refundable &&
    refundDetailsAvailable &&
    refundableCredits === purchasedCredits &&
    maximumRefundAmountInCents === order.totalFee
  const fullRefundEligible = fullRefundAvailable && order.refundAllowed && order.fullRefundAllowed
  const partialRefundAvailable = refundable && refundDetailsAvailable && refundableCredits > 0
  const partialRefundEligible = partialRefundAvailable && order.refundAllowed
  const manualRefundAvailable =
    refundable &&
    new Date(order.updatedAt).getTime() < Date.now() - manualRefundMinimumOrderAgeInMilliseconds
  const manualRefundEligible = manualRefundAvailable && order.refundAllowed

  const onFullRefund = () => {
    Modal.confirm({
      title: translate('orderHistories.refund'),
      content: translate('orderHistories.fullRefundWarning'),
      okButtonProps: { danger: true },
      onOk: () => execute(() => refundOrder(order.id)),
    })
  }

  const onRefundFormFinish = (request: OrderHistoryRefundRequest) => {
    const refund = refundMode === 'manual' ? manuallyRefundOrder : partiallyRefundOrder
    return execute(() => refund(order.id, request))
  }

  return (
    <>
      <Row gutter={[16, 16]}>
        <Col xs={24} xl={16}>
          <Card
            title={translate('orderHistories.detailTitle')}
            extra={
              <AuthPermission permission={PermissionsEnum.ORDER_HISTORIES_WRITE}>
                <Space orientation="horizontal">
                  {(refundable || manualPending) && (
                    <>
                      {manualPending && refundDetailsAvailable ? (
                        <Button
                          type="primary"
                          danger
                          disabled={!order.refundAllowed}
                          onClick={() => setRefundMode('manual')}
                        >
                          {translate('orderHistories.confirmManualRefund')}
                        </Button>
                      ) : (
                        <>
                          {fullRefundAvailable && (
                            <Button
                              type="primary"
                              danger
                              disabled={!fullRefundEligible}
                              onClick={onFullRefund}
                            >
                              {translate('orderHistories.refund')}
                            </Button>
                          )}
                          {partialRefundAvailable && (
                            <Button
                              type="primary"
                              danger
                              disabled={!partialRefundEligible}
                              onClick={() => setRefundMode('partial')}
                            >
                              {translate('orderHistories.partialRefund')}
                            </Button>
                          )}
                          {manualRefundAvailable && (
                            <Button
                              disabled={!manualRefundEligible}
                              onClick={() => execute(() => markOrderManualRefundPending(order.id))}
                            >
                              {translate('orderHistories.switchToManualRefund')}
                            </Button>
                          )}
                        </>
                      )}
                    </>
                  )}
                  {planPurchase &&
                    ['PAID', 'PENDING_REFUND', 'PENDING_PARTIAL_REFUND'].includes(
                      order.status ?? '',
                    ) && (
                      <Button onClick={() => execute(() => refreshRefundStatus(order.id))}>
                        {translate('orderHistories.refreshRefundStatus')}
                      </Button>
                    )}
                </Space>
              </AuthPermission>
            }
          >
            {!planPurchase && (
              <Alert
                type="info"
                showIcon
                message={translate('orderHistories.benefitRefundNote')}
                style={{ marginBottom: 16 }}
              />
            )}
            {(refundable || manualPending) &&
              !order.refundAllowed &&
              order.refundBlockedReason === 'NEWER_PLAN_SUBSCRIPTION_ORDER_EXISTS' && (
                <Alert
                  type="warning"
                  showIcon
                  message={translate('orderHistories.refundBlockedByNewerOrder', {
                    orderId: order.latestRefundableOrderHistoryId ?? '-',
                  })}
                  style={{ marginBottom: 16 }}
                />
              )}
            {refundable &&
              order.refundAllowed &&
              !order.fullRefundAllowed &&
              order.fullRefundBlockedReason === 'ACTIVE_TOP_UP_CREDITS_EXIST' && (
                <Alert
                  type="warning"
                  showIcon
                  message={translate('orderHistories.fullRefundBlockedByActiveTopUp')}
                  style={{ marginBottom: 16 }}
                />
              )}
            <Descriptions
              column={1}
              bordered
              size="small"
              styles={{ label: { fontWeight: 600, width: 200 } }}
            >
              <Descriptions.Item label={translate('orderHistories.orderId')}>
                {order.id}
              </Descriptions.Item>
              <Descriptions.Item label={translate('orderHistories.user')}>
                <Button type="link" href={`${guavapassUrl}/admin/users/${order.userId}`}>
                  {order.userName
                    ? `User#${order.userId} ${order.userName}`
                    : `User#${order.userId}`}
                </Button>
              </Descriptions.Item>
              <Descriptions.Item label={translate('orderHistories.orderType')}>
                {translate(
                  planPurchase ? 'orderHistories.planPurchase' : 'orderHistories.benefitBooking',
                )}
              </Descriptions.Item>
              <Descriptions.Item label={translate('orderHistories.plan')}>
                {order.planId != null ? (
                  <LinkButton to={`/plans/${order.planId}`}>
                    {order.planExternalId ?? order.planId}
                  </LinkButton>
                ) : (
                  '-'
                )}
              </Descriptions.Item>
              {order.benefitBooking && (
                <>
                  <Descriptions.Item label={translate('orderHistories.benefitPlatform')}>
                    {order.benefitBooking.benefitPlatformName} (#
                    {order.benefitBooking.benefitPlatformId})
                  </Descriptions.Item>
                  <Descriptions.Item label={translate('orderHistories.lessonId')}>
                    {order.benefitBooking.lessonId ?? '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label={translate('orderHistories.bookingId')}>
                    {order.benefitBooking.bookingId ?? '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label={translate('orderHistories.paymentMode')}>
                    {translate(
                      order.benefitBooking.paymentMode === 'FULLY_COVERED'
                        ? 'orderHistories.fullyCovered'
                        : 'orderHistories.userPay',
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label={translate('orderHistories.originalAmount')}>
                    ¥{order.benefitBooking.baseCashCents / 100}
                  </Descriptions.Item>
                  <Descriptions.Item label={translate('orderHistories.subsidyAmount')}>
                    ¥{order.benefitBooking.subsidyCashCents / 100}
                  </Descriptions.Item>
                </>
              )}
              <Descriptions.Item label={translate('orderHistories.planSubscription')}>
                {order.planSubscription ? (
                  <LinkButton to={`/plan-subscriptions/${order.planSubscription.id}`}>
                    {order.planSubscription.id}
                  </LinkButton>
                ) : (
                  '-'
                )}
              </Descriptions.Item>
              <Descriptions.Item label={translate('orderHistories.creditBatch')}>
                {order.creditBatch ? (
                  <LinkButton to={`/credit-batches/${order.creditBatch.id}`}>
                    {`${order.creditBatch.remainingCredits}/${order.creditBatch.issuedCredits}`}
                  </LinkButton>
                ) : (
                  '-'
                )}
              </Descriptions.Item>
              <Descriptions.Item label={translate('orderHistories.status')}>
                {formatOrderHistoryStatus(order.status)}
              </Descriptions.Item>
              <Descriptions.Item label={translate('orderHistories.couponCode')}>
                {order.couponTemplateId ? (
                  <LinkButton to={`/coupon-templates/${order.couponTemplateId}`}>
                    {order.couponCode}
                  </LinkButton>
                ) : (
                  (order.couponCode ?? '-')
                )}
              </Descriptions.Item>
              <Descriptions.Item label={translate('orderHistories.stackedCouponCode')}>
                {order.stackedCouponTemplateId ? (
                  <LinkButton to={`/coupon-templates/${order.stackedCouponTemplateId}`}>
                    {order.stackedCouponCode}
                  </LinkButton>
                ) : (
                  (order.stackedCouponCode ?? '-')
                )}
              </Descriptions.Item>
              <Descriptions.Item label={translate('orderHistories.referralCode')}>
                {order.referralCode ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label={translate('orderHistories.prepayId')}>
                {order.prepayId ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label={translate('orderHistories.nonceStr')}>
                {order.nonceStr}
              </Descriptions.Item>
              <Descriptions.Item label={translate('orderHistories.outTradeNo')}>
                {order.outTradeNo}
              </Descriptions.Item>
              <Descriptions.Item label={translate('orderHistories.transactionId')}>
                {order.transactionId ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label={translate('orderHistories.notifyUrl')}>
                {order.notifyUrl}
              </Descriptions.Item>
              <Descriptions.Item label={translate('orderHistories.totalFee')}>
                {order.totalFee == null ? '-' : `¥${order.totalFee / 100}`}
              </Descriptions.Item>
              <Descriptions.Item label={translate('orderHistories.maximumRefundCredits')}>
                {refundDetailsAvailable ? refundableCredits : '-'}
              </Descriptions.Item>
              <Descriptions.Item label={translate('orderHistories.maximumRefundAmount')}>
                {refundDetailsAvailable ? `¥${maximumRefundAmountInCents / 100}` : '-'}
              </Descriptions.Item>
              <Descriptions.Item label={translate('orderHistories.tradeType')}>
                {order.tradeType}
              </Descriptions.Item>
              <Descriptions.Item label={translate('orderHistories.outRefundNo')}>
                {order.outRefundNo ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label={translate('orderHistories.refundAmount')}>
                {order.refundFee == null ? '-' : `¥${order.refundFee / 100}`}
              </Descriptions.Item>
              <Descriptions.Item label={translate('orderHistories.refundId')}>
                {order.refundId ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label={translate('orderHistories.createdBy')}>
                {order.createdBy ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label={translate('orderHistories.createdAt')}>
                {getDateTime(order.createdAt)}
              </Descriptions.Item>
              <Descriptions.Item label={translate('orderHistories.updatedBy')}>
                {order.updatedBy ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label={translate('orderHistories.updatedAt')}>
                {getDateTime(order.updatedAt)}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
        <Col xs={24} xl={8}>
          <Card title={translate('orderHistories.referralInvite')}>
            {order.referralInvite ? (
              <Descriptions column={1} bordered size="small">
                <Descriptions.Item label={translate('orderHistories.referralInviteId')}>
                  {order.referralInvite.id}
                </Descriptions.Item>
                <Descriptions.Item label={translate('orderHistories.referralInviteSender')}>
                  <Button
                    type="link"
                    href={`${guavapassUrl}/admin/users/${order.referralInvite.senderId}`}
                  >
                    {order.referralInvite.senderName
                      ? `User#${order.referralInvite.senderId} ${order.referralInvite.senderName}`
                      : `User#${order.referralInvite.senderId}`}
                  </Button>
                </Descriptions.Item>
                <Descriptions.Item label={translate('orderHistories.referralCode')}>
                  {order.referralInvite.code}
                </Descriptions.Item>
                <Descriptions.Item label={translate('orderHistories.referralInviteExpiredAt')}>
                  {order.referralInvite.expiredAt
                    ? getDateTime(order.referralInvite.expiredAt)
                    : ''}
                </Descriptions.Item>
                <Descriptions.Item label={translate('orderHistories.createdAt')}>
                  {getDateTime(order.referralInvite.createdAt)}
                </Descriptions.Item>
              </Descriptions>
            ) : (
              translate('orderHistories.noReferralInvite')
            )}
          </Card>
        </Col>
      </Row>
      {planPurchase && refundMode && refundDetailsAvailable && (
        <OrderRefundModal
          mode={refundMode}
          order={order}
          onCancel={() => setRefundMode(undefined)}
          onFinish={onRefundFormFinish}
        />
      )}
    </>
  )
}

export default OrderHistoryDetails
